import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
	UntypedFormControl,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { get as _get } from 'lodash';
import { CrmUser, CrmLanguage } from '@app-models/index';
import * as fromAuth from '@app-stores/reducers/auth';
import * as Auth from '@app-stores/actions/auth.action';
import { AppService } from '@app-services/app.service';
import { AuthService } from '@app-services/auth.service';
import { RoleService } from '@app-services/role.service';
import { environment } from '../../../../environments/environment';
import {TranslationService} from "@app-services/translation.service";
@Component({
	selector: 'crm-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
	loginForm: UntypedFormGroup;
	user: CrmUser;
	loggedIn = false;
	loading = false;
	errorMessage: string = '';
	getLoginPending$ = this._store.pipe(select(fromAuth.getLoginPending));
	getLoggedIn$ = this._store.pipe(select(fromAuth.getLoggedIn));
	getLoginError$ = this._store.pipe(select(fromAuth.getLoginError));
	user$ = this._store.pipe(select(fromAuth.getUser));
	crmLanguage_id: number;
	crmLanguages: CrmLanguage[] = [];
	crmAllLanguages: CrmLanguage[] = [];
	rememberMe = new UntypedFormControl(false);
	submitted = false;
	showPassword = false;
	isTranslationLoaded = false;
	environment = environment;
	// Private
	private _viewInited: boolean = false;
	private _unsubscribeAll: Subject<any>;
	constructor(
		private _appService: AppService,
		private _authService: AuthService,
		private _router: Router,
		private _route: ActivatedRoute,
		private _formBuilder: UntypedFormBuilder,
		private _store: Store<fromAuth.AuthState>,
		private _role: RoleService,
		private _translationService: TranslationService
	) {
		// Set the private defaults
		this.loginForm = this._formBuilder.group({
			emailOrUsername: ['', [Validators.required]],
			password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(255)]],
		});
		this._unsubscribeAll = new Subject();
	}
	ngOnInit() {
		const locale = this.getRequestedLocale();
		let loadedLocale = localStorage.getItem('translationLanguage');
		if (loadedLocale) {
			loadedLocale = JSON.parse(loadedLocale);
		}
		this.isTranslationLoaded = locale == loadedLocale;

		this.getLoginPending$.pipe(takeUntil(this._unsubscribeAll)).subscribe((res) => {
			this.loading = res;
		});
		this.getLoggedIn$.pipe(takeUntil(this._unsubscribeAll)).subscribe((res) => {
			if (!this._viewInited) return;
			this.loggedIn = res;
			const user = JSON.parse(localStorage.getItem('user'));
			if (user) {
				if (user.user_role === 'Community Leader') {
					this._router.navigate(['/pages/home/view']);
				} else {
					this._router.navigate(['/pages/home/view']);
				}
			}
		});
		this.getLoginError$.pipe(takeUntil(this._unsubscribeAll)).subscribe((res) => {
			if (!this._viewInited) return;
			if (res) {
				this.submitted = false;
				this.errorMessage = res;
			}
		});
		this.user$.subscribe((res) => {
			this.user = res;
			const allLanguages = {
				code_1: '',
				code_2_t: '',
				code_2_b: '',
				language_name: 'All Languages',
				status: 'D',
				id: -1,
			};
			this.crmLanguages = [allLanguages, ...(_get(res, 'languages') || [])];
			this.crmLanguages = this.crmLanguages.map(lang =>  {
				return {...lang,
					language_name: this._translationService.translate(`Languages.${lang.language_name.replace(/\s/g, '')}`)
				}
			});
		});
		this._viewInited = true;
	}
	login() {
		if (this.loginForm.invalid) {
			return;
		} else {
			this.submitted = true;
			this.errorMessage = null;
			this._store.dispatch(
				new Auth.Login(this.loginForm.value, this.rememberMe.value),
			);
		}
	}
	getUserLanguagesByEmail(email) {
		this._authService.getUserLanguagesByEmail(email).pipe(takeUntil(this._unsubscribeAll)).subscribe((res) => {
			if (!this._viewInited) return;
			if (res && res['data']) {
				this.crmLanguages = res['data'];
			}
		});
	}
	getStoredLanguages() {
		const language = localStorage.getItem('language');
		if (language) {
			const user = JSON.parse(localStorage.getItem('user'));
			if (user.user_role === 'Community Leader') {
				this._router.navigate(['/pages/home/view']);
			} else {
				this._router.navigate(['/pages/home/view']);
			}
		}
	}
	onLanguageSet() {
		const user = JSON.parse(localStorage.getItem('user'));
		this._store.dispatch(
			new Auth.SelectLanguage()
		);
		if (user.user_role === 'Community Leader') {
			this._router.navigate(['/pages/home/view']);
		} else {
			this._router.navigate(['/pages/home/view']);
		}
	}
	onChangeLanguage(e) {
		this.crmLanguage_id = e;
		if (e === -1) {
			this._appService.currentLanguage = null;
			localStorage.removeItem('language');
		} else {
			this._appService.currentLanguage = this.crmLanguages.find(
				(l) => l.id === this.crmLanguage_id,
			);
			localStorage.setItem('language', JSON.stringify(this._appService.currentLanguage));
		}
	}
	ngOnDestroy(): void {
		// Unsubscribe from all subscriptions
		this._unsubscribeAll.next();
		this._unsubscribeAll.complete();
	}
	toggleShowPassword() {
		this.showPassword = !this.showPassword;
	}
	getRequestedLocale() {
		let languageCode = null;
		if (location && location.search) {
			const params = location.search.substr(1).split('&');
			for (const i in params) {
				const param = params[i].split('=');
				if (param[0] === 'lang') {
					languageCode = param[1];
				}
			}
		}

		let locale = 'en';
		if (languageCode) {
			locale = languageCode.substr(0, 2).toLowerCase();
		} else if (navigator) {
			locale = navigator.language;
			locale = locale.substr(0, 2).toLowerCase();
		} else {
			locale = 'en';
		}
		return locale;
	}
}
